import { ArrowLeftRight, History, Info, LayoutDashboard, Settings2, ShieldCheck } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { ConnectionBadge } from '@/components/layout/connection-badge';
import { Logo } from '@/components/layout/logo';
import { ThemeToggle } from '@/components/layout/theme-toggle';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/stores/session-store';
import { isActiveTransfer, useTransferStore } from '@/stores/transfer-store';

const NAV = [
  { to: '/', label: 'Home', icon: LayoutDashboard },
  { to: '/transfers', label: 'Transfers', icon: ArrowLeftRight },
  { to: '/history', label: 'History', icon: History },
  { to: '/settings', label: 'Settings', icon: Settings2 },
] as const;

const MOBILE_NAV = [
  { to: '/', label: 'Home', icon: LayoutDashboard },
  { to: '/transfers', label: 'Transfers', icon: ArrowLeftRight },
  { to: '/history', label: 'History', icon: History },
  { to: '/settings', label: 'Settings', icon: Settings2 },
  { to: '/about', label: 'About', icon: Info },
] as const;

function SessionCard() {
  const phase = useSessionStore((state) => state.phase);
  const room = useSessionStore((state) => state.room);
  const peers = useSessionStore((state) => state.peers);

  if (!room || (phase !== 'connected' && phase !== 'reconnecting')) {
    return (
      <div className="rounded-xl border border-border/60 bg-muted/25 p-3">
        <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
          <ShieldCheck className="size-3.5 text-primary/80" />
          Private by default
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground/70">
          Files travel device to device. Nothing is stored on a server.
        </p>
      </div>
    );
  }

  const connected = Object.values(peers).filter((peer) => peer.connectionState === 'connected').length;

  return (
    <NavLink
      to={`/r/${room.code}`}
      className="group block rounded-xl border border-primary/20 bg-primary/[0.06] p-3 transition-colors hover:border-primary/35 hover:bg-primary/[0.09]"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-[11px] font-medium text-primary">
          <span className="status-dot bg-primary" />
          {phase === 'reconnecting' ? 'Reconnecting' : 'In a room'}
        </span>
        <span className="font-mono text-[10px] tracking-wider text-primary/70">{room.code}</span>
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        {connected} device{connected === 1 ? '' : 's'} linked
      </p>
    </NavLink>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <div className="min-h-dvh">
      <div className="app-bg" />

      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[252px] flex-col border-r border-border/60 bg-background/55 px-4 py-5 backdrop-blur-xl lg:flex">
        <div className="px-1">
          <Logo />
        </div>

        <nav className="mt-9 flex flex-col gap-1" aria-label="Primary">
          {NAV.map((item) => {
            const active =
              item.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn(
                  'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors',
                  active
                    ? 'bg-card text-foreground shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset]'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                )}
                aria-current={active ? 'page' : undefined}
              >
                <span
                  className={cn(
                    'absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full bg-primary transition-opacity',
                    active ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <item.icon
                  className={cn(
                    'size-[17px] transition-colors',
                    active ? 'text-primary' : 'text-muted-foreground/80 group-hover:text-foreground',
                  )}
                />
                {item.label}
                {item.to === '/transfers' ? <TransferCountBadge /> : null}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col gap-3">
          <SessionCard />
          <div className="flex items-center justify-between rounded-xl px-1 py-1">
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <NavLink
                to="/about"
                className="inline-flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="About FlowShare"
              >
                <Info className="size-[15px]" />
              </NavLink>
            </div>
            <span className="pr-1 font-mono text-[10px] text-muted-foreground/50">v1.0</span>
          </div>
        </div>
      </aside>

      <div className="lg:pl-[252px]">
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-border/50 bg-background/70 px-4 py-3 backdrop-blur-xl lg:hidden">
          <Logo />
          <div className="flex items-center gap-1.5">
            <ConnectionBadge className="hidden xs:inline-flex" />
            <ThemeToggle />
          </div>
        </div>

        <main className="mx-auto w-full max-w-[1180px] px-4 pb-28 pt-6 sm:px-6 lg:px-10 lg:pb-16 lg:pt-10">
          <div key={location.pathname} className="animate-page-in">
            {children}
          </div>
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border/60 bg-background/85 pb-safe backdrop-blur-xl lg:hidden"
        aria-label="Primary"
      >
        <div className="mx-auto flex max-w-md items-stretch justify-between px-2 pt-1.5">
          {MOBILE_NAV.map((item) => {
            const active =
              item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn(
                  'relative flex min-w-0 flex-1 flex-col items-center gap-1 rounded-lg px-1 py-1.5 text-[10px] font-medium transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground',
                )}
                aria-current={active ? 'page' : undefined}
              >
                <item.icon className={cn('size-[18px]', active && 'drop-shadow-[0_0_10px_var(--glow)]')} />
                <span className="truncate">{item.label}</span>
                {active ? (
                  <span className="absolute -top-1.5 h-[2px] w-5 rounded-full bg-primary" />
                ) : null}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function TransferCountBadge() {
  const hasActive = useTransferStore((state) =>
    Object.values(state.transfers).some(isActiveTransfer),
  );
  if (!hasActive) return null;
  return <span className="ml-auto size-1.5 rounded-full bg-primary" aria-label="Active transfers" />;
}
